import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, Filter, RefreshCw, Calendar, Euro } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoicesManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextNumber, setNextNumber] = useState('');

  useEffect(() => {
    loadInvoices();
    loadNextNumber();
  }, []);

  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  const loadNextNumber = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/invoices/next-number`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNextNumber(data.next_invoice_number);
      }
    } catch (error) {
      console.error('Error loading next invoice number:', error);
    }
  };

  const downloadInvoice = async (orderId, language = 'es') => {
    try {
      const response = await fetch(`${API_URL}/api/invoice/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          language: language,
          client_country: 'Spain'
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura-${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Factura descargada');
      } else {
        toast.error('Error al descargar factura');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Error al descargar factura');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
            <FileText className="w-8 h-8 text-[#D4AF37]" />
            <span>Facturas</span>
          </h1>
          <p className="text-gray-400 mt-1">Gestiona las facturas generadas</p>
        </div>
        
        {/* Next Invoice Number */}
        <div className="px-4 py-2 bg-[#1a1d23] rounded-lg border border-[#2a3441]">
          <p className="text-xs text-gray-400">Próxima factura</p>
          <p className="text-lg font-mono text-[#D4AF37]">{nextNumber}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-xl border border-[#2a3441]">
          <p className="text-sm text-gray-400">Total Facturas</p>
          <p className="text-2xl font-bold text-white">{invoices.length}</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-xl border border-[#2a3441]">
          <p className="text-sm text-gray-400">Total Facturado</p>
          <p className="text-2xl font-bold text-[#D4AF37]">
            €{invoices.reduce((sum, inv) => sum + (inv.total || 0), 0).toLocaleString()}
          </p>
        </div>
        <div className="p-4 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-xl border border-[#2a3441]">
          <p className="text-sm text-gray-400">IVA Recaudado</p>
          <p className="text-2xl font-bold text-[#8B5CF6]">
            €{invoices.reduce((sum, inv) => sum + (inv.tax_amount || 0), 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2a3441]">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Nº Factura
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  País
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Subtotal
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  IVA
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a3441]">
              {invoices.map((invoice) => (
                <tr key={invoice.invoice_id} className="hover:bg-[#1a1d23]/50">
                  <td className="px-6 py-4">
                    <span className="font-mono text-[#D4AF37]">{invoice.invoice_number}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-white font-medium">{invoice.client_company || invoice.client_name}</p>
                      <p className="text-xs text-gray-400">{invoice.client_tax_id || 'Sin NIF'}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-300">{invoice.client_country}</td>
                  <td className="px-6 py-4 text-gray-300">€{(invoice.subtotal || 0).toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <span className={`${invoice.tax_rate > 0 ? 'text-[#8B5CF6]' : 'text-gray-400'}`}>
                      {invoice.tax_rate > 0 ? `${(invoice.tax_rate * 100).toFixed(0)}%` : 'Exento'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-white font-semibold">€{(invoice.total || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">
                    {new Date(invoice.created_at).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => downloadInvoice(invoice.order_id, invoice.language)}
                      className="p-2 hover:bg-[#D4AF37]/10 rounded-lg transition-colors group"
                      title="Descargar PDF"
                    >
                      <Download className="w-4 h-4 text-gray-400 group-hover:text-[#D4AF37]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {invoices.length === 0 && (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-[#3a3d42] mx-auto mb-4" />
            <p className="text-gray-400">No hay facturas generadas</p>
            <p className="text-sm text-gray-500 mt-1">Las facturas aparecerán aquí después de completar pedidos</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesManagement;
